import { useMemo, useState } from 'react';
import { STAGE_DEFS, type ArbiterEvent, type TurnTimeline } from '@voicer/shared';
import { useStore } from '../state/store';
import { Waterfall } from './Waterfall';

const STAGE_LABEL: Record<string, string> = Object.fromEntries(STAGE_DEFS.map((d) => [d.key, d.label]));
STAGE_LABEL.tokensPerSec = 'Generation speed (tok/s)';

export function Dashboard() {
  const turns = useStore((s) => s.turns);
  const summary = useStore((s) => s.summary);
  const events = useStore((s) => s.arbiterEvents);
  const status = useStore((s) => s.status);
  const audio = useStore((s) => s.audio);
  const scheduler = useStore((s) => s.scheduler);
  const background = useStore((s) => s.background);
  const settings = useStore((s) => s.settings);
  const [selected, setSelected] = useState<string | null>(null);

  const recent = useMemo(() => [...turns].slice(-30).reverse(), [turns]);
  const current = recent.find((t) => t.turnId === selected) ?? recent[0];
  const maxP95 = Math.max(1, ...(summary?.stages.filter((s) => s.stage !== 'tokensPerSec').map((s) => s.p95) ?? [1]));

  return (
    <div className="panel-grid">
      <div className="panel wide">
        <h3>
          Latest turn waterfall
          <span className="spacer" />
          <select value={current?.turnId ?? ''} onChange={(e) => setSelected(e.target.value)}>
            {recent.map((t) => (
              <option key={t.turnId} value={t.turnId}>
                {new Date(t.startedAt).toLocaleTimeString()} · {t.mode} · {t.genTokens} tok{t.interrupted ? ' · interrupted' : ''}
              </option>
            ))}
          </select>
        </h3>
        {current ? <Waterfall turn={current} /> : <div className="dim">No turns yet.</div>}
      </div>

      <div className="panel">
        <h3>Stage latency (P50 / P95)</h3>
        {summary ? (
          <div className="bars">
            {summary.stages.map((s) => (
              <div className="bar-row" key={s.stage}>
                <span title={STAGE_LABEL[s.stage]}>{STAGE_LABEL[s.stage] ?? s.stage}</span>
                {s.stage === 'tokensPerSec' ? (
                  <span className="dim">{s.count ? `${s.p50.toFixed(1)} tok/s median` : '-'}</span>
                ) : (
                  <div className="bar">
                    <div className="p95" style={{ width: `${Math.min(100, (s.p95 / maxP95) * 100)}%` }} />
                    <div className="p50" style={{ width: `${Math.min(100, (s.p50 / maxP95) * 100)}%` }} />
                  </div>
                )}
                <span className="dim num">
                  {s.count ? `${Math.round(s.p50)} / ${Math.round(s.p95)} ms (n=${s.count})` : '-'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="dim">Waiting for metrics…</div>
        )}
      </div>

      <div className="panel">
        <h3>Time to first audio trend</h3>
        <Trend turns={turns} />
      </div>

      <div className="panel">
        <h3>Barge-in arbiter</h3>
        {summary ? (
          <div className="kv">
            <span className="k">events</span>
            <span>{summary.arbiter.total}</span>
            <span className="k">by outcome</span>
            <span>
              {Object.entries(summary.arbiter.byOutcome)
                .map(([k, v]) => `${k} ${v}`)
                .join(' · ') || '-'}
            </span>
            <span className="k">by tier</span>
            <span>
              {Object.entries(summary.arbiter.byTier)
                .map(([k, v]) => `${k} ${v}`)
                .join(' · ') || '-'}
            </span>
            <span className="k">onset to duck P50</span>
            <span>{summary.arbiter.onsetToDuckP50 !== null ? `${Math.round(summary.arbiter.onsetToDuckP50)} ms` : '-'}</span>
            <span className="k">onset to stop P50</span>
            <span>{summary.arbiter.onsetToStopP50 !== null ? `${Math.round(summary.arbiter.onsetToStopP50)} ms` : '-'}</span>
            <span className="k">Tier B classify P50</span>
            <span>{summary.arbiter.classifyP50 !== null ? `${Math.round(summary.arbiter.classifyP50)} ms` : '-'}</span>
            <span className="k">false interruptions</span>
            <span>
              {summary.arbiter.falseInterrupts}
              {summary.arbiter.total ? ` (${((summary.arbiter.falseInterrupts / summary.arbiter.total) * 100).toFixed(0)}%)` : ''}
            </span>
            <span className="k">missed interruptions</span>
            <span>{summary.arbiter.missedInterrupts}</span>
          </div>
        ) : (
          <div className="dim">-</div>
        )}
      </div>

      <div className="panel">
        <h3>Model providers</h3>
        {status?.providers ? (
          <div className="kv">
            <span className="k">LLM</span>
            <span>
              {status.providers.llm.provider}:{status.providers.llm.model}{' '}
              <span style={{ color: status.providers.llm.reachable ? 'var(--ok)' : 'var(--bad)' }}>
                {status.providers.llm.reachable ? 'ok' : status.providers.llm.error ?? 'down'}
              </span>
            </span>
            <span className="k">STT</span>
            <span>
              {status.providers.stt.provider}:{status.providers.stt.model}{' '}
              <span style={{ color: status.providers.stt.reachable ? 'var(--ok)' : 'var(--bad)' }}>
                {status.providers.stt.reachable ? 'ok' : status.providers.stt.error ?? 'down'}
              </span>
            </span>
            <span className="k">TTS</span>
            <span>
              {status.providers.tts.provider}:{status.providers.tts.model}{' '}
              <span style={{ color: status.providers.tts.reachable ? 'var(--ok)' : 'var(--bad)' }}>
                {status.providers.tts.reachable ? 'ok' : status.providers.tts.error ?? 'down'}
              </span>
            </span>
          </div>
        ) : (
          <div className="dim">-</div>
        )}
      </div>

      <div className="panel">
        <h3>KoboldCPP</h3>
        {status ? (
          <div className="kv">
            <span className="k">url</span>
            <span>{status.url}</span>
            <span className="k">reachable</span>
            <span style={{ color: status.reachable ? 'var(--ok)' : 'var(--bad)' }}>{status.reachable ? 'yes' : status.error ?? 'no'}</span>
            <span className="k">version</span>
            <span>{status.version ?? '-'}</span>
            <span className="k">model</span>
            <span>{status.model ?? '-'}</span>
            <span className="k">template</span>
            <span>
              {status.template ?? '-'} {settings.instructTemplate === 'auto' ? '(auto)' : ''}
            </span>
            <span className="k">features</span>
            <span>
              {Object.entries(status.flags)
                .filter(([, v]) => v)
                .map(([k]) => k)
                .join(', ') || '-'}
            </span>
            <span className="k">max context</span>
            <span>{status.maxContext ?? '-'}</span>
            <span className="k">busy</span>
            <span>{status.perf ? (status.perf.idle ? 'idle' : 'busy') : '-'}</span>
            <span className="k">kobold queue</span>
            <span>{status.perf?.queue ?? '-'}</span>
            <span className="k">last prefill / eval</span>
            <span>{status.perf ? `${status.perf.last_process.toFixed(2)}s / ${status.perf.last_eval.toFixed(2)}s (${status.perf.last_token_count} tok)` : '-'}</span>
            <span className="k">total gens</span>
            <span>{status.perf?.total_gens ?? '-'}</span>
            <span className="k">uptime</span>
            <span>{status.perf ? `${Math.round(status.perf.uptime / 60)} min` : '-'}</span>
            <span className="k">voices</span>
            <span>{status.voices.length ? status.voices.slice(0, 12).join(', ') + (status.voices.length > 12 ? '…' : '') : '-'}</span>
          </div>
        ) : (
          <div className="dim">-</div>
        )}
      </div>

      <div className="panel">
        <h3>Scheduler</h3>
        <div className="kv">
          <span className="k">running</span>
          <span>{scheduler?.running ? `${scheduler.running.kind} (${scheduler.running.sinceMs} ms)` : 'idle'}</span>
          <span className="k">queued</span>
          <span>{scheduler?.queued.length ? scheduler.queued.map((q) => `${q.kind} p${q.priority} ${q.waitedMs}ms`).join(' · ') : 'none'}</span>
          <span className="k">last queue wait</span>
          <span>
            {scheduler && Object.keys(scheduler.recentQueueWaitMs).length
              ? Object.entries(scheduler.recentQueueWaitMs)
                  .map(([k, v]) => `${k} ${v}ms`)
                  .join(' · ')
              : '-'}
          </span>
        </div>
        <h3 style={{ marginTop: 12 }}>Background jobs</h3>
        <div className="stack" style={{ fontSize: 12 }}>
          {background.length ? (
            background
              .slice(-6)
              .reverse()
              .map((b, i) => (
                <div key={i}>
                  <span className="dim">{new Date(b.ts).toLocaleTimeString()}</span> {b.job} <b>{b.state}</b> {b.detail && <span className="dim">{b.detail}</span>}
                </div>
              ))
          ) : (
            <span className="dim">none yet</span>
          )}
        </div>
      </div>

      <div className="panel">
        <h3>Audio</h3>
        <div className="kv">
          <span className="k">microphone</span>
          <span style={{ color: audio.micReady ? 'var(--ok)' : 'var(--warn)' }}>{audio.micReady ? 'ready' : audio.micError ?? 'not started'}</span>
          <span className="k">mic level (RMS)</span>
          <span>{audio.micLevel.toFixed(4)}</span>
          <span className="k">noise floor</span>
          <span>{audio.noiseFloor.toFixed(4)}</span>
          <span className="k">VAD probability</span>
          <span>{audio.vadProb.toFixed(2)}</span>
          <span className="k">echo correlation</span>
          <span style={{ color: audio.echoChronic ? 'var(--warn)' : undefined }}>
            {audio.echoCorr.toFixed(2)} {audio.echoChronic ? '(chronic: use a headset)' : ''}
          </span>
          <span className="k">output base latency</span>
          <span>{audio.baseLatencyMs.toFixed(1)} ms</span>
          <span className="k">output device latency</span>
          <span>{audio.outputLatencyMs.toFixed(1)} ms</span>
          <span className="k">mode</span>
          <span>{settings.mode}</span>
        </div>
      </div>

      <div className="panel wide">
        <h3>
          Arbiter events
          <span className="spacer" />
          <button className="small" onClick={() => download('voicer-arbiter.csv', eventsCsv(events))}>
            export CSV
          </button>
          <button className="small" onClick={() => download('voicer-turns.json', JSON.stringify({ turns, events }, null, 2))}>
            export JSON
          </button>
        </h3>
        <table className="data">
          <thead>
            <tr>
              <th>time</th>
              <th>outcome</th>
              <th>tier</th>
              <th>transcript</th>
              <th>assistant was saying</th>
              <th className="num">duck</th>
              <th className="num">pause</th>
              <th className="num">decide</th>
              <th className="num">stop</th>
              <th className="num">stt</th>
              <th className="num">stt wait</th>
              <th className="num">llm</th>
              <th>feedback</th>
            </tr>
          </thead>
          <tbody>
            {[...events]
              .slice(-40)
              .reverse()
              .map((e) => (
                <tr key={e.id} className={`outcome-${e.outcome}`}>
                  <td>{new Date(e.ts).toLocaleTimeString()}</td>
                  <td>
                    <span className="badge" style={{ marginLeft: 0 }}>
                      {e.outcome}
                    </span>
                  </td>
                  <td>{e.tier}</td>
                  <td>{e.transcript}</td>
                  <td className="dim">{e.currentSentence.slice(0, 60)}</td>
                  <td className="num">{ms(e.onsetToDuckMs)}</td>
                  <td className="num">{ms(e.onsetToPauseMs ?? null)}</td>
                  <td className="num">{ms(e.onsetToDecisionMs)}</td>
                  <td className="num">{ms(e.onsetToStopMs)}</td>
                  <td className="num">{ms(e.sttMs)}</td>
                  <td className="num">{ms(e.sttQueueWaitMs)}</td>
                  <td className="num">{ms(e.classifyMs)}</td>
                  <td>{e.feedback === 'wrong' ? <span style={{ color: 'var(--bad)' }}>wrong</span> : ''}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ms(v: number | null) {
  return v === null ? '-' : `${Math.round(v)}`;
}

function Trend({ turns }: { turns: TurnTimeline[] }) {
  const pts = turns
    .slice(-40)
    .map((t) => {
      const a = t.marks.speechEnd ?? t.marks.turnCommitted;
      const b = t.marks.firstAudioOut;
      return a !== undefined && b !== undefined ? b - a : null;
    })
    .filter((v): v is number => v !== null);
  if (!pts.length) return <div className="dim">-</div>;
  const max = Math.max(...pts, 1);
  return (
    <div>
      <div className="sparkline">
        {pts.map((v, i) => (
          <div key={i} style={{ height: `${(v / max) * 100}%` }} title={`${v} ms`} />
        ))}
      </div>
      <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
        last {pts[pts.length - 1]} ms · best {Math.min(...pts)} ms · worst {max} ms · {pts.length} turns
      </div>
    </div>
  );
}

function eventsCsv(events: ArbiterEvent[]): string {
  const cols: (keyof ArbiterEvent)[] = ['ts', 'turnId', 'outcome', 'tier', 'transcript', 'currentSentence', 'onsetToDuckMs', 'onsetToPauseMs', 'onsetToDecisionMs', 'onsetToStopMs', 'sttMs', 'sttQueueWaitMs', 'classifyMs', 'classifyQueueWaitMs', 'feedback'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [cols.join(','), ...events.map((e) => cols.map((c) => esc(e[c])).join(','))].join('\n');
}

export function download(name: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
